/**
 * 智能文案系统 - 表单初始化Hook
 */

import { useState, useEffect, useRef } from 'react';
import { App } from 'antd';
import { smartCopywritingAPI } from './services';
import type { BrandBrief, SKUData, ProductInfoChangeData } from './types';

interface UseSmartCopywritingFormProps {
    skuId?: number;
    skuData?: SKUData;
    loadProject: (id: number) => void;
    onDataChange?: (data: ProductInfoChangeData) => void;
}

export const useSmartCopywritingForm = ({
    skuId,
    skuData,
    loadProject,
    onDataChange,
}: UseSmartCopywritingFormProps) => {
    const { message } = App.useApp();

    // 项目状态
    const [projectId, setProjectId] = useState<number | null>(null);
    const [brands, setBrands] = useState<BrandBrief[]>([]);
    const [initialized, setInitialized] = useState(false);

    // 表单状态
    const [productName, setProductName] = useState('');
    const [sellingPoints, setSellingPoints] = useState<string[]>(['', '', '']);
    const [keywordsBySite, setKeywordsBySite] = useState<Record<string, string>>({ US: '' });
    const [competitorTitles, setCompetitorTitles] = useState('');
    const [selectedBrandId, setSelectedBrandId] = useState<number | undefined>();
    const [customAudienceInput, setCustomAudienceInput] = useState('');

    // 加载品牌列表
    useEffect(() => {
        fetch('/api/brands/')
            .then(res => res.ok ? res.json() : null)
            .then(data => data && setBrands(data.results || data || []))
            .catch(err => console.error('加载品牌列表失败', err));
    }, []);

    const hasShownRestoreMsg = useRef(false);

    // 自动加载已存在的项目
    useEffect(() => {
        if (!skuId || projectId) return;

        smartCopywritingAPI.getProjectBySku(skuId).then(existingProject => {
            if (existingProject) {
                setProjectId(existingProject.id);
                loadProject(existingProject.id);

                if (existingProject.product_name) setProductName(existingProject.product_name);
                if (existingProject.selling_points?.length) setSellingPoints(existingProject.selling_points);
                if (existingProject.competitor_titles?.length) setCompetitorTitles(existingProject.competitor_titles.join('\n'));
                if (existingProject.keywords_by_site) setKeywordsBySite(existingProject.keywords_by_site);
                if (existingProject.brand) setSelectedBrandId(existingProject.brand);

                if (!hasShownRestoreMsg.current) {
                    message.info(`已恢复项目进度（第 ${existingProject.current_step} 步）`);
                    hasShownRestoreMsg.current = true;
                }
            }
        }).catch(err => console.error('加载现有项目失败:', err));
    }, [skuId, projectId, loadProject, message]);

    // 从SKU数据自动填充表单
    useEffect(() => {
        if (!skuData || initialized) return;

        if (skuData.product_name || skuData.name) {
            setProductName(skuData.product_name || skuData.name || '');
        }
        if (skuData.brand_obj || typeof skuData.brand === 'number') {
            setSelectedBrandId(skuData.brand_obj || skuData.brand as number);
        }

        // 合并基本卖点和策略USP
        let combinedPoints = skuData.selling_points || [];
        if (skuData.operation_strategy_data?.primary_usp) {
            const usp = skuData.operation_strategy_data.primary_usp;
            if (!combinedPoints.includes(usp)) {
                // 将USP放在最前面
                combinedPoints = [usp, ...combinedPoints];
            }
        }
        // 确保数组不为空
        if (combinedPoints.length === 0) combinedPoints = ['', '', ''];
        setSellingPoints(combinedPoints);

        // 预填自定义人群维度
        if (skuData.operation_strategy_data?.target_audience) {
            setCustomAudienceInput(skuData.operation_strategy_data.target_audience);
        }

        if (skuData.site_keywords?.length) {
            const keywordsMap: Record<string, string> = {};
            skuData.site_keywords.forEach(item => {
                if (item.site && item.keywords) keywordsMap[item.site] = item.keywords;
            });
            setKeywordsBySite(keywordsMap);
        } else if (skuData.site) {
            setKeywordsBySite({ [skuData.site]: '' });
        }

        setInitialized(true);
    }, [skuData, initialized]);

    // 数据变更通知
    const notifyDataChange = (updates: Partial<{
        productName: string;
        sellingPoints: string[];
        keywordsBySite: Record<string, string>;
    }>) => {
        if (!onDataChange) return;
        const data: ProductInfoChangeData = {};
        if (updates.productName !== undefined) data.product_name = updates.productName;
        if (updates.sellingPoints !== undefined) data.selling_points = updates.sellingPoints.filter(p => p.trim());

        if (updates.keywordsBySite !== undefined) {
            data.site_keywords = Object.entries(updates.keywordsBySite)
                .filter(([_, keywords]) => keywords.trim())
                .map(([site, keywords]) => ({ site, keywords }));
        }
        onDataChange(data);
    };

    const handleProductNameChange = (value: string) => {
        setProductName(value);
        notifyDataChange({ productName: value });
    };

    const handleSellingPointsChange = (points: string[]) => {
        setSellingPoints(points);
        notifyDataChange({ sellingPoints: points });
    };

    const handleKeywordsBySiteChange = (keywords: Record<string, string>) => {
        setKeywordsBySite(keywords);
        notifyDataChange({ keywordsBySite: keywords });
    };

    // 获取选中的品牌
    const selectedBrand = brands.find(b => b.id === selectedBrandId);

    return {
        projectId,
        setProjectId,
        brands,
        productName,
        setProductName: handleProductNameChange,
        sellingPoints,
        setSellingPoints: handleSellingPointsChange,
        keywordsBySite,
        setKeywordsBySite: handleKeywordsBySiteChange,
        competitorTitles,
        setCompetitorTitles,
        selectedBrandId,
        setSelectedBrandId,
        selectedBrand,
        customAudienceInput,
        setCustomAudienceInput,
    };
};

export default useSmartCopywritingForm;
